import { EditComponentProps } from "@material-table/core";
import { WebAssetSharp } from "@mui/icons-material";
import {
  Box,
  createTheme,
  InputAdornment,
  TextField,
  TextFieldProps,
  ThemeProvider,
} from "@mui/material";
import { SCancelButton } from "components/buttons/SCancelButton";
import { SDialog, SDialogProps, SInputProps } from "components/parts";
import { STheme } from "components/styles";
import { labels } from "messages/labels";
import { useState } from "react";
import { locationCd } from "services";

type Props = Partial<Omit<TextFieldProps, "size" | "type">> &
  Pick<SInputProps, "align"> &
  Partial<SDialogProps> & {
    editProps: EditComponentProps<any>;
  } & Partial<{
    size: number;
    location: boolean;
  }>;

export const STextCell = ({
  ref,
  value,
  defaultValue,
  align,
  dialog,
  editProps,
  size,
  location = false,
  handleSubmit: submit,
  ...props
}: Props): JSX.Element => {
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);
  const handleSubmit = (data: any) => {
    submit?.call(this, data);
    setOpen(false);
  };
  const action = (
    <SCancelButton label={labels.buttons.close} onClick={handleClose} />
  );
  const hasDialog =
    typeof dialog !== "undefined" &&
    typeof submit !== "undefined" &&
    !props.disabled;

  // テーマ設定
  const theme = createTheme(
    {
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 0,
              paddingRight: "3px",
              backgroundColor: STheme.fields.normal,
              borderColor: STheme.fields.border,
              "&.Mui-disabled": {
                color: STheme.fields.text,
                backgroundColor: STheme.fields.disabled,
              },
            },
            input: {
              textAlign: align,
              fontSize: "1rem",
              paddingLeft: "4px",
              paddingRight: "0px",
              paddingTop: "1px",
              paddingBottom: "0px",
              color: STheme.fields.text,
              "&.Mui-disabled": {
                fontSize: "0.9rem",
                WebkitTextFillColor: STheme.fields.text,
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              height: "auto",
              margin: 0,
            },
          },
        },
      },
    },
    { ...STheme }
  );
  return (
    <ThemeProvider theme={theme}>
      <TextField
        {...props}
        value={value}
        defaultValue={defaultValue}
        inputRef={ref}
        inputProps={{ ...props.inputProps, maxLength: size, tabIndex: 0 }}
        onChange={(event) => {
          const value = event.currentTarget.value;
          const defaultValue = event.currentTarget.defaultValue;
          if (location) {
            event.currentTarget.value = locationCd(value, defaultValue);
          }
          props.onChange?.call(this, event);
          editProps.onChange(event.currentTarget.value);
        }}
        onBlur={(event) => {
          props.onBlur?.call(this, event);
          editProps.onChange(event.currentTarget.value);
        }}
        InputProps={
          hasDialog
            ? {
                ...props.InputProps,
                endAdornment: (
                  <Box tabIndex={-1}>
                    <InputAdornment
                      position="end"
                      tabIndex={-1}
                      onClick={handleOpen}
                    >
                      <WebAssetSharp fontSize="medium" />
                    </InputAdornment>
                  </Box>
                ),
              }
            : props.InputProps
        }
        autoComplete="off"
        fullWidth
      />
      {hasDialog && (
        <SDialog
          open={open}
          dialog={dialog}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          action={action}
        />
      )}
    </ThemeProvider>
  );
};
